import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  FaGithub,
  FaExternalLinkAlt,
  FaFilter,
  FaTimes,
  FaCode,
} from "react-icons/fa";

const ProjectModal = ({ project, isOpen, onClose }) => {
  if (!project || !isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-md"
        onClick={onClose}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative mt-16 bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 rounded-2xl w-full max-w-3xl overflow-hidden shadow-2xl z-10 border border-emerald-400/20"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-red-600 z-10 bg-gray-800/90 rounded-full p-2 shadow-lg transition-all duration-300 hover:shadow-xl"
        >
          <FaTimes size={24} />
        </button>

        <div className="relative h-72">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          {/* <h3 className="absolute bottom-6 left-8 text-3xl font-bold text-red-500 mb-4 font-display">
            {project.title}
          </h3> */}
        </div>

        <div className="p-4">
          <h3 className="relative text-2xl font-bold text-gray-100 mb-3 font-['Inter']">
            {project.title}
          </h3>
          <p className="text-gray-400 mb-8 leading-relaxed font-['Inter'] text-md">
            {project.description}
          </p>

          <div className="mb-8">
            <h4 className="text-lg font-semibold text-emerald-400 mb-4 font-['Inter']">
              Technologies Used
            </h4>
            <div className="flex flex-wrap gap-3">
              {project.technologies.map((tech, idx) => (
                <span
                  key={idx}
                  className="px-4 py-2 text-xs font-medium rounded-xl bg-emerald-400/10 text-emerald-400 border border-emerald-400/20 flex items-center gap-2 hover:bg-emerald-400/20 transition-all duration-300"
                >
                  <FaCode className="text-emerald-300" />
                  {tech}
                </span>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-4">
            <motion.a
              whileHover={{ scale: 1.02 }}
              href={project.github}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-100 transition-colors shadow-lg shadow-emerald-400/20"
            >
              <FaGithub size={18} />
              View Code
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.02 }}
              href={project.live}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-gray-100 transition-all shadow-xl shadow-emerald-400/20"
            >
              <FaExternalLinkAlt size={18} />
              Live Demo
            </motion.a>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const Projects = () => {
  const [filter, setFilter] = useState("all");
  const [selectedProject, setSelectedProject] = useState(null);

  const projects = [
    {
      title: "SplitEase",
      shortDescription:
        "Simplify group expenses with a seamless, real-time cost-splitting and payment solution.",
      description:
        "Managing shared expenses often leads to confusion, awkward reminders, and delayed settlements. Built with Next.js for an intuitive UI and Express.js with MongoDB for a scalable backend, it leverages WebSockets for real-time notifications and updates when expenses are added or settled. The application implements Redis for advanced caching, session management, and pub/sub functionality, ensuring lightning-fast performance even with complex expense calculations. Users can securely settle debts through integrated payment gateways, receiving instant confirmations across devices. Enhanced with robust JWT authentication and OAuth 2.0 integration, SplitEase prioritizes data security while providing a seamless experience. Perfect for roommates tracking monthly bills, friends planning trips, or any group needing to manage shared finances with precision and transparency.",
      technologies: [
        "Next.js",
        "Express.js",
        "MongoDB",
        "WebSockets",
        "Redis",
        "OAuth2.0",
        "Material UI",
        "Payment Gateway",
      ],
      github: "https://github.com/CodeTirtho97/SplitEase_frontend",
      live: "https://split-ease-v1-tirth.vercel.app/",
      image: "/images/projects/SplitEase.png",
      category: "fullstack",
    },
    {
      title: "ResumeGenius AI - Resume Analyzer & ATS Optimizer",
      shortDescription:
        "An AI-driven resume optimizer that enhances your chances of landing your dream job.",
      description:
        "Many resumes get rejected due to poor ATS (Applicant Tracking System) compatibility. ResumeGenius AI bridges this gap by analyzing and optimizing resumes using OpenAI’s GPT-4. It extracts key details like skills, experience, and education, comparing them against job descriptions to improve alignment. The React-based UI offers a seamless experience, while Node.js & Express power the backend. MongoDB ensures secure resume storage with automatic deletion for privacy compliance. Future plans include automated interview question generation.",
      technologies: [
        "React",
        "MaterialUI",
        "NodeJS",
        "Express",
        "OpenAI API",
        "Framer Motion",
      ],
      github: "https://github.com/CodeTirtho97/ResumeGenius_AI",
      live: "https://resume-genius-ai.vercel.app/",
      image: "/images/projects/ResumeGenius_AI.png",
      category: "tools",
    },
    {
      title: "AutoReadMe - Instant README Generator",
      shortDescription:
        "A CLI tool that auto-generates structured README files for developers.",
      description:
        "Writing README files is essential for open-source contributions but often time-consuming. AutoReadMe automates this by generating structured, professional README files through an interactive CLI. Using Chalk for console styling and Figlet for ASCII headers, it detects project dependencies, formats documentation, and supports multiple customizable templates. Future enhancements include auto-detection of project licenses and badge integration.",
      technologies: ["JavaScript", "NPM", "CLI", "Chalk", "Figlet"],
      github: "https://github.com/CodeTirtho97/AutoREADME",
      live: "https://www.npmjs.com/package/autoreadme-cli",
      image: "/images/projects/AutoReadMe.png",
      category: "tools",
    },
    {
      title: "Decentralized GitFL - Git-Inspired Federated Learning",
      shortDescription:
        "A revolutionary peer-to-peer federated learning framework eliminating central coordination with Git-inspired version control.",
      description:
        "Traditional federated learning faces critical bottlenecks: central servers slow down training, device heterogeneity creates delays, and model staleness degrades performance. Decentralized GitFL revolutionizes this by applying Git's distributed version control principles to machine learning. Built with PyTorch and custom peer-to-peer networking, it enables direct device-to-device model sharing without central coordination. The system uses reinforcement learning for intelligent peer selection and sophisticated algorithms for handling outdated models. Achieving relatively higher accuracy and much faster training compared to traditional approaches, this research breakthrough enables scalable, privacy-preserving AI collaboration across IoT networks, edge computing, and distributed environments where centralized control is impractical.",
      technologies: [
        "Python",
        "Reinforcement Learning",
        "P2P Networking",
        "Distributed Systems",
        "Socket Programming",
      ],
      github: "https://github.com/CodeTirtho97/Decentralized_GitFL",
      live: "https://github.com/CodeTirtho97/Decentralized_GitFL",
      image: "/images/projects/Decentralized_GitFL.png",
      category: "ml",
    },
    {
      title: "CodeRush",
      shortDescription:
        "Stay ahead in competitive programming with real-time contest tracking and reminders.",
      description:
        "Competitive programmers often struggle to track upcoming contests across multiple platforms. CodeRush streamlines this by providing a centralized dashboard that fetches real-time contest data from CodeForces, CodeChef, and AtCoder. It features a modern UI with dark mode, built using Material UI & Tailwind CSS, and fetches data securely via REST APIs & a proxy server. Users can set one-click reminders and add contests to Google Calendar seamlessly. Future enhancements include automated performance tracking based on past contest participation.",
      technologies: [
        "Chrome Extension API",
        "JavaScript",
        "Material UI",
        "Tailwind CSS",
        "Express.js (Proxy Server)",
        "REST APIs",
        "Google Calendar API",
      ],
      github: "https://github.com/CodeTirtho97/CodeRush",
      live: "https://chromewebstore.google.com/detail/coderush-competitive-codi/bjafblmmcmbojhmfgailcgppbgnjmejl?authuser=0&hl=en",
      image: "/images/projects/CodeRush.png",
      category: "tools",
    },
    {
      title: "MovieMate - Movie Recommendation Engine",
      shortDescription:
        "An AI-powered recommendation system that understands your movie preferences.",
      description:
        "Finding the right movie can be time-consuming. MovieMate leverages machine learning to deliver personalized recommendations based on viewing history and user preferences. Using content-based and collaborative filtering, it processes large datasets efficiently with TF-IDF vectorization and cosine similarity. Matrix factorization (SVD) further enhances recommendation accuracy. Built with Python, Pandas, and NumPy, it provides interactive insights via Jupyter Notebook. Future improvements include real-time user feedback integration.",
      technologies: ["Python", "ML", "Pandas", "Numpy", "Jupyter"],
      github: "https://github.com/CodeTirtho97/MovieMate",
      live: "https://github.com/CodeTirtho97/MovieMate",
      image: "/images/projects/MovieMate_poster2.png",
      category: "ml",
    },
    {
      title: "RouteGenie - Personal Travel Assistant",
      shortDescription:
        "An intelligent travel planner that optimizes your trips with AI-powered insights.",
      description:
        "Travel planning can be overwhelming, from managing routes to tracking budgets. RouteGenie simplifies itinerary creation with AI-driven route optimization, real-time weather updates, and cost estimation. Developed using React (frontend) and Node.js & Express (backend), it seamlessly integrates with Google Maps, weather forecasting APIs, and hotel booking services. MongoDB efficiently handles user preferences and saved itineraries. Future improvements include AI-based trip suggestions and smart budget recommendations.",
      technologies: ["React", "MongoDB", "NodeJS", "Express", "MaterialUI"],
      github: "https://github.com/CodeTirtho97/Route-Genie",
      live: "https://route-genie-tirth.vercel.app/",
      image: "/images/projects/Route_Genie_2.png",
      category: "fullstack",
    },
    {
      title: "Speech Emotion Recognition Engine",
      shortDescription:
        "An AI-driven tool that detects emotions from speech with real-time analysis.",
      description:
        "Understanding emotions from voice data has vast applications in customer service, mental health, and virtual assistants. This Speech Emotion Recognition Engine extracts Mel-Frequency Cepstral Coefficients (MFCCs) and spectrogram features using Librosa, then applies ML classifiers (SVM, Random Forest, KNN) to detect emotions. Cross-validation and hyperparameter tuning ensure accuracy. Future enhancements include real-time emotion tracking for AI-driven sentiment analysis.",
      technologies: ["Python", "ML", "Pandas", "Librosa", "Jupyter"],
      github: "https://github.com/CodeTirtho97/Speech_Emotion_Recognition",
      live: "https://github.com/CodeTirtho97/Speech_Emotion_Recognition",
      image: "/images/projects/Speech_Emotion_Recognition_Poster.png",
      category: "ml",
    },
    {
      title: "GitHub Finder",
      shortDescription:
        "A powerful GitHub profile search tool with real-time data fetching.",
      description:
        "Exploring developer profiles on GitHub can be cumbersome. GitHub Finder simplifies this by allowing users to search and analyze GitHub profiles with real-time data. Built with React and Tailwind CSS, it features a clean UI, search filtering options, dark mode, and caching for optimized performance. The integration with the GitHub API provides seamless access to repository stats and contributions. Future plans include AI-driven profile insights.",
      technologies: ["React", "API", "Tailwind-CSS"],
      github: "https://github.com/CodeTirtho97/Github-Finder",
      live: "https://github-finder-tirth.vercel.app/",
      image: "/images/projects/Github_Finder.png",
      category: "frontend",
    },
    {
      title: "Hangman - Classic Word Guessing Game",
      shortDescription:
        "A fun and interactive Hangman game with a sleek UI and engaging gameplay.",
      description:
        "Classic games like Hangman remain timeless, offering an enjoyable way to improve vocabulary and problem-solving skills. This React-based Hangman game features smooth animations, responsive design, and an interactive UI using Tailwind CSS. Players must guess the hidden word within a limited number of attempts, with visual progress displayed dynamically. Local storage support ensures users can resume unfinished games. Future enhancements include multiplayer mode and a leaderboard system to increase engagement.",
      technologies: ["React", "Tailwind CSS", "JavaScript"],
      github: "https://github.com/CodeTirtho97/Hangman",
      live: "https://hangman-v2-tirth.vercel.app/",
      image: "/images/projects/Hangman_Game.png",
      category: "frontend",
    },
    {
      title: "Dad Jokes - Random Joke Generator",
      shortDescription:
        "A simple and fun random joke generator with API integration and smooth UI.",
      description:
        "Laughter is the best medicine, and Dad Jokes delivers it with a simple, API-driven joke generator. This lightweight web app, built with React and Tailwind CSS, fetches jokes dynamically using the Official Joke API. The sleek and minimalist UI enhances the user experience, while one-click joke refreshing and copy-to-clipboard features add convenience. Future updates include personalized joke recommendations based on user preferences and a text-to-speech option.",
      technologies: ["React", "API", "Tailwind CSS"],
      github: "https://github.com/CodeTirtho97/Dad-Jokes",
      live: "https://dad-jokes-v2-tirth.vercel.app/",
      image: "/images/projects/Dad_Jokes.png",
      category: "frontend",
    },
  ];

  const categories = [
    { id: "all", name: "All Projects" },
    { id: "fullstack", name: "Full Stack" },
    { id: "ml", name: "Machine Learning" },
    { id: "frontend", name: "Frontend" },
    { id: "tools", name: "Dev Tools" },
  ];

  const filteredProjects =
    filter === "all"
      ? projects
      : projects.filter((project) => project.category === filter);

  return (
    <div className="min-h-screen bg-gray-900 py-28 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Subtle Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 opacity-95"></div>
      <div className="max-w-7xl mx-auto relative z-10">
        <motion.h2
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-4xl md:text-6xl font-bold text-center font-['Inter'] mb-12 text-gray-100"
        >
          <span className="text-emerald-400 hover:cursor-default">
            Featured Creations
          </span>
          <p className="text-white text-lg font-bold mt-2 font-['Inter'] hover:cursor-default">
            Bringing ideas to life through code
          </p>
        </motion.h2>

        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <motion.button
              key={category.id}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setFilter(category.id)}
              className={`px-6 py-2.5 rounded-xl font-medium font-['Inter'] transition-all duration-300 ${
                filter === category.id
                  ? "bg-emerald-400/20 text-emerald-400 border border-emerald-400 shadow-md shadow-emerald-200/20"
                  : "bg-gray-800/20 text-gray-400 hover:bg-gray-700/20 hover:text-emerald-400 border border-emerald-400/40 hover:border-emerald-400/20"
              }`}
            >
              <span className="flex items-center gap-2">
                <FaFilter className="text-emerald-300 text-xs" />
                {category.name}
              </span>
            </motion.button>
          ))}
        </div>

        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {filteredProjects.map((project, index) => (
            <motion.div
              key={project.title}
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="group bg-gray-800/20 rounded-xl overflow-hidden shadow-lg hover:border-emerald-400/20 hover:shadow-xl hover:bg-gray-700/20 transition-all duration-300 border flex flex-col transform hover:-translate-y-1"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>

              <div className="p-6 flex-1 flex flex-col">
                <h3 className="text-lg font-bold font-['Inter'] text-gray-100 mb-2 group-hover:text-emerald-400 transition-colors">
                  {project.title}
                </h3>
                <p className="text-gray-400 text-sm mb-4 flex-1 font-['Inter'] font-light">
                  {project.shortDescription}
                </p>

                <div className="flex flex-wrap gap-2 mb-6">
                  {project.technologies.slice(0, 3).map((tech, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 text-xs font-medium font-['Inter'] rounded-lg bg-emerald-400/10 text-emerald-400 border border-emerald-400/20"
                    >
                      {tech}
                    </span>
                  ))}
                  {project.technologies.length > 3 && (
                    <span className="px-3 py-1 text-xs font-medium font-['Inter'] rounded-lg bg-gray-700/20 text-gray-400 border border-gray-700/20">
                      +{project.technologies.length - 3}
                    </span>
                  )}
                </div>

                <div className="flex justify-between items-center mt-auto">
                  <div className="flex gap-4">
                    <motion.a
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.2 }}
                      className="text-gray-400 hover:text-emerald-400 transition-colors"
                    >
                      <FaGithub size={22} />
                    </motion.a>
                    <motion.a
                      href={project.live}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.2 }}
                      className="text-gray-400 hover:text-emerald-400 transition-colors"
                    >
                      <FaExternalLinkAlt size={20} />
                    </motion.a>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    className="px-4 py-2 text-sm font-medium font-['Inter'] text-emerald-400 hover:bg-emerald-400/20 hover:text-emerald-300 border border-emerald-400/20 rounded-lg transition-all"
                    onClick={() => setSelectedProject(project)}
                  >
                    Learn More
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <ProjectModal
          project={selectedProject}
          isOpen={!!selectedProject}
          onClose={() => setSelectedProject(null)}
        />
      </div>
    </div>
  );
};

export default Projects;
